import { createRoot, createEffect } from "solid-js";
import { localGet, localSet } from "@/shared/safeStorage";
import { createStore } from "solid-js/store";
import { api, type CampaignConfig, type CampaignRates } from "@/api/client";

export interface TenantConfig {
  id: string;
  name: string;
  logoUrl: string;
  logoAlt: string;
  primaryColor: string;
  primaryColorLight: string;
  primaryColorDark: string;
  accentColor: string;
  navBackground: string;
  navTextColor: string;
  isDefaultTenant: boolean;
  badgeColor: string;
  contactEmail: string;
  contactPhone: string;
  supplierLogoUrl: string | null;
  supplierLogoAlt: string | null;
  features: { multiProduct: boolean; principalSwitcher: boolean };
  /**
   * Only the two fields the chrome needs out of the tenant's tracking section.
   *
   * <p>Narrower than the wire shape on purpose. The tracking layer reads its own copy through
   * `readTrackingConfig`, and widening this to the full section would give every component that
   * happens to have the theme store a second, subtly different view of the measurement setup.</p>
   *
   * <p>Both survive `TenantTrackingConfig.Disabled()`, which is what makes them safe here: a host
   * outside the tenant's allowlist has its measurement stripped, and the footer would otherwise lose
   * the company name and the privacy link along with it.</p>
   */
  tracking?: { privacyPolicyUrl: string | null; controllerName: string | null };
  /**
   * The promotion running on this site right now, or null.
   *
   * <p>Resolved server-side against the clock, so this is simply "is there a campaign" — nothing here
   * compares dates, and a campaign that ends while a tab is open keeps showing until the tenant config
   * is fetched again. That is the right trade for a promotion: a browser clock is the visitor's to set,
   * and the price the server puts on the wire is the one that counts either way.</p>
   */
  campaign?: CampaignConfig | null;
}

export type ColorMode = "light" | "dark";

interface ThemeState {
  tenant: TenantConfig;
  colorMode: ColorMode;
  loaded: boolean;
}

const DEFAULT_TENANT: TenantConfig = {
  id: "lobas",
  name: "LOBAS® PORTEN",
  logoUrl: "https://files-eu-prod.cms.commerce.dynamics.com/cms/api/gkvlchkxcr/binary/MD1vEN",
  logoAlt: "LOBAS® PORTEN",
  primaryColor: "#3392A3",
  primaryColorLight: "#4DAABB",
  primaryColorDark: "#267685",
  // Not #2D2D2D any more: this now colours text, and near-black text on the app's dark ground is
  // text nobody can read. The value is the primary colour, which is what that text was drawn in
  // before the setting existed.
  accentColor: "#3392A3",
  // The same navy index.css and the register hold, so the bars do not flash grey for the moment
  // between first paint and the tenant answering.
  navBackground: "#001b31",
  navTextColor: "#FFFFFF",
  // Until the server answers, assume LOBAS' own site. It is what the hardcoded values above already
  // describe, and the alternative default — an unnamed partner — would render a page crediting a
  // supplier it has no logo for.
  isDefaultTenant: true,
  contactEmail: "lobas@lobas.no",
  contactPhone: "56 19 31 00",
  badgeColor: "#3392A3",
  supplierLogoUrl: null,
  supplierLogoAlt: null,
  features: { multiProduct: false, principalSwitcher: false },
  tracking: { privacyPolicyUrl: "/personvern", controllerName: null },
  // No campaign until the server says otherwise. The opposite default would flash a promotion on every
  // first paint and take it away again a moment later.
  campaign: null,
};

/** Parse hex to RGB triplet string: "#FF0000" → "255 0 0" */
function hexToRgb(hex: string): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `${r} ${g} ${b}`;
}

function createThemeStore() {
  const savedMode = localGet("color-mode") as ColorMode | null;

  const [state, setState] = createStore<ThemeState>({
    tenant: DEFAULT_TENANT,
    // App is dark-only (LOBAS PORTEN design). Default to dark unless the user
    // has explicitly chosen otherwise.
    colorMode: savedMode ?? "dark",
    loaded: false,
  });

  // ── Apply CSS variables when tenant changes ──────────────────────────
  createEffect(() => {
    const t = state.tenant;
    const root = document.documentElement;

    // What each of the tenant's colour settings drives here, so nobody has to grep to find out:
    //   PrimaryColor       → --accent, --accent-rgb, --color-primary (fills, borders, washes)
    //   PrimaryColorDark   → --accent-hover (hover fills and the dark end of the brand gradients)
    //   AccentColor        → --text-accent (text and icons in the brand colour)
    //   BadgeColor         → --badge (the "+X kr" badges)
    //   NavBackground      → --color-nav-bg (the bars, and the opaque branded surfaces)
    //   NavTextColor       → --tenant-nav-text (text on all of those)
    //   PrimaryColorLight  → --accent-light (the light end of the brand gradients; the sign-in
    //                        page, which the server renders, reads it too)
    root.style.setProperty("--color-primary", hexToRgb(t.primaryColor));
    // Text drawn in the brand colour — headings, figures, the icons beside them. Its own setting
    // because it is READ rather than pressed: a hue that carries a button can be too loud for a
    // hundred small labels, and too quiet for one. AccentColor already existed and drove nothing;
    // this is what it means now.
    root.style.setProperty("--text-accent", t.accentColor);

    // The "+X kr" badges and the pills built to match them.
    root.style.setProperty("--badge", t.badgeColor);
    // The top and bottom bars. A triplet, not the raw hex, because the bars are translucent — see
    // .bg-nav in index.css.
    root.style.setProperty("--color-nav-bg", hexToRgb(t.navBackground));
    root.style.setProperty("--tenant-nav-text", t.navTextColor);

    // Mockup "LOBAS PORTEN" accent vars (hex + comma-RGB for rgba()).
    root.style.setProperty("--accent", t.primaryColor);
    root.style.setProperty("--accent-hover", t.primaryColorDark);
    root.style.setProperty("--accent-light", t.primaryColorLight);
    root.style.setProperty("--accent-rgb", hexToRgb(t.primaryColor).replaceAll(" ", ", "));
  });

  // ── Apply dark/light mode ────────────────────────────────────────────
  createEffect(() => {
    const mode = state.colorMode;
    document.documentElement.classList.toggle("dark", mode === "dark");
    localSet("color-mode", mode);
  });

  // ── Actions ──────────────────────────────────────────────────────────

  async function loadTenant() {
    try {
      const config = await api.tenant.config();
      setState("tenant", { ...DEFAULT_TENANT, ...config });
    } catch (e) {
      console.warn("Failed to load tenant config, using defaults:", e);
    } finally {
      setState("loaded", true);
    }
  }

  function toggleColorMode() {
    setState("colorMode", (m) => (m === "light" ? "dark" : "light"));
  }

  function setColorMode(mode: ColorMode) {
    setState("colorMode", mode);
  }

  return {
    state,
    loadTenant,
    toggleColorMode,
    setColorMode,
  };
}

// ── Singleton ────────────────────────────────────────────────────────────────

const themeStore = createRoot(createThemeStore);

export const themeState = themeStore.state;
export const loadTenant = themeStore.loadTenant;
export const toggleColorMode = themeStore.toggleColorMode;

/**
 * The promotion this site is running, or null.
 *
 * <p>One accessor rather than reading the store directly, because every consumer asks the same
 * question and half of them ask it in a `Show when=`. It is also the seam where "is there a campaign"
 * could grow a condition — a per-principal rule, say — without twelve call sites learning about it.</p>
 */
export const activeCampaign = (): CampaignConfig | null => themeState.tenant.campaign ?? null;

/**
 * Whether the campaign discounts every product group by the same percentage.
 *
 * <p>Decides how the promotion is WORDED. "Spar 20 %" is only true when it is 20 % on everything; a
 * campaign that cuts openers and leaves the door alone has to say something vaguer and let the price
 * table carry the detail.</p>
 */
export const campaignIsUniform = (c: CampaignRates): boolean =>
  c.garageDoorDiscount === c.doorOpenerDiscount
  && c.doorOpenerDiscount === c.accessoryDiscount
  && c.accessoryDiscount === c.exteriorDoorDiscount
  && c.exteriorDoorDiscount === c.openerAccessoryDiscount;

/** App-wide feature flag (from appsettings "FeatureFlags:MultiProduct") — gates orders that hold more
 *  than one product: several garage doors, and an exterior door beside a garage door. */
export const multiProductEnabled = () => themeState.tenant.features?.multiProduct ?? false;

/** App-wide feature flag (from appsettings "FeatureFlags:PrincipalSwitcher") — exposes the admin-only
 *  Basic/Dealer/Internal dev switcher on this deployment (e.g. the test instance). */
export const principalSwitcherEnabled = () => themeState.tenant.features?.principalSwitcher ?? false;
